import {lazy,Suspense,useRef,useState} from 'react';
const Purchase=lazy(()=>import('./InstantInspirePurchase'));
import {prepareStudioPhoto,studioService} from '../../services/blissDesignStudioService';
import type {StudioRoom} from '../../config/blissDesignStudio';
import type {LandingModel} from './landing-models';
import {INSPIRATION_NOTICE} from './RoomPreviewChoices';

export default function InstantInspire({model}:{model:LandingModel}) {
 const camera=useRef<HTMLInputElement>(null),picker=useRef<HTMLInputElement>(null),locked=useRef(false);
 const [room,setRoom]=useState<StudioRoom|null>(null),[busy,setBusy]=useState(false),[error,setError]=useState(''),[prompt,setPrompt]=useState(''),[idea,setIdea]=useState('');
 const [original,setOriginal]=useState('');
 const [purchasing,setPurchasing]=useState(false);
 const placementNote=model.collection==='Basic'?`Place the complete ladder and upper-bunk entry at the ${model.ladderFlipped?'left':'right'} short end, viewed from the long side of the bed.`:'';
 const changeLimit=500-(placementNote?placementNote.length+1:0);
 const latest=room?.looks.filter(look=>look.status==='ready'&&look.imageUrl).at(-1);
 async function generate(current:StudioRoom,changes:string){
  const result=await studioService.inspire(current.id,model.id,[placementNote,changes].filter(Boolean).join('\n'),crypto.randomUUID());
  const ready=result.room.looks.some(look=>look.status==='ready'&&look.imageUrl);
  setRoom(result.room);
  if(!ready)throw new Error('Your image is still being prepared. Please try again shortly.');
  setIdea(changes);setPrompt('');
 }
 async function upload(file?:File){
  if(!file||locked.current)return;
  locked.current=true;setBusy(true);setError('');
  try{const photo=await prepareStudioPhoto(file);const result=await studioService.createInspireRoom(photo,model.id);setRoom(result.room);setOriginal(photo);setIdea('');setPrompt('');await generate(result.room,'');}
  catch(e){setError(e instanceof Error?e.message:'Please try another photo.');}
  finally{locked.current=false;setBusy(false);}
 }
 async function update(){
  if(!room||locked.current)return;
  const next=prompt.trim()? [idea,prompt.trim()].filter(Boolean).join('\n'):idea;
  if(next.length>changeLimit){setError('Please shorten your changes to leave room for your selected bed layout.');return;}
  locked.current=true;setBusy(true);setError('');
  try{await generate(room,next);}catch(e){setError(e instanceof Error?e.message:'Please try again.');}finally{locked.current=false;setBusy(false);}
 }
 return <section className="inspire-panel" aria-labelledby="instant-inspire-heading" aria-busy={busy}>
  <h3 id="instant-inspire-heading">Instant Inspire</h3>
  <p>{model.name} · Take one photo. We’ll create your room image.</p>
  <div className="inspire-upload-actions">
   <button type="button" disabled={busy} onClick={()=>camera.current?.click()}>Take a photo</button>
   <button type="button" disabled={busy} onClick={()=>picker.current?.click()}>{room?'Change photo':'Upload a photo'}</button>
  </div>
  <input hidden ref={camera} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" aria-label="Take Instant Inspire room photo" onChange={e=>{void upload(e.target.files?.[0]);e.target.value='';}}/>
  <input hidden ref={picker} type="file" accept="image/jpeg,image/png,image/webp" aria-label="Upload Instant Inspire room photo" onChange={e=>{void upload(e.target.files?.[0]);e.target.value='';}}/>
  {!room&&<p className="inspire-notice">JPG, PNG or WebP · up to 15 MB</p>}
  {busy&&<p className="inspire-status" role="status">{latest?'Updating your image…':'Creating your room image…'} This may take a minute or two.</p>}
  {error&&<p role="alert">{error}</p>}
  {(latest?.imageUrl||original)&&<div className="inspire-result">
   <img src={latest?.imageUrl||original} alt={latest?`${model.name} inspiration in your room`:'Your original room photo'}/>
   {latest&&<div className="inspire-buy"><button type="button" className="preview-choice-button preview-choice-olive" disabled={busy} onClick={()=>setPurchasing(true)}><span>Get My Price →</span></button><p>Your image and selected bed stay with your project.</p></div>}
   {latest&&<form className="inspire-editor" onSubmit={e=>{e.preventDefault();void update();}}>
    <label htmlFor="inspire-changes">What would you like to change?</label>
    <textarea id="inspire-changes" value={prompt} maxLength={changeLimit} disabled={busy} onChange={e=>setPrompt(e.target.value)} placeholder="Make the bedding navy and add a coastal rug."/>
    <button className="preview-choice-button preview-choice-gold" type="submit" disabled={busy}><span>{busy?'Updating…':'Update Image'}</span></button>
    <p className="inspire-notice">Leave blank to try another version.</p>
   </form>}
  </div>}
  {!latest&&room&&!busy&&<button type="button" className="inspire-retry" onClick={()=>void update()}>Try again</button>}
  <p className="inspire-notice">{INSPIRATION_NOTICE}</p>
  {latest&&room&&<Suspense fallback={purchasing?<p role="status">Opening your next step…</p>:null}><Purchase key={room.id} open={purchasing} onClose={()=>setPurchasing(false)} model={model} room={room} look={latest}/></Suspense>}
 </section>;
}

